/** Knowledge Base documents and facts for the selected organization or project. */
import '../library-view.css';

import { Button } from '@kerfjs/ui/button';
import { space } from '@kerfjs/ui/css-values';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';

import {
  libraryDocs,
  libraryFactDialogId,
  libraryFacts,
  libraryRevisions,
  libraryScope,
  libraryTab,
  notice,
  shellSearch,
} from '../../data/state.js';
import { matchesSettingsSearch } from '../../lib/settings-search.js';
import { DocumentPreviewDialog } from '../demand-editor/components/document-preview-dialog/index.js';
import { LibraryDocuments } from './components/library-documents.js';
import { LibraryFactDialog } from './components/library-fact-dialog.js';
import { LibraryFacts } from './components/library-facts.js';

export function LibraryView(): SafeHtml {
  const accepted = libraryFacts.value.filter((fact) => !fact.proposed);
  const facts = accepted.filter((fact) => {
    const source = libraryDocs.value.find((doc) => doc.id === fact.sourceDocumentId);
    return matchesSettingsSearch([fact.text, fact.category, source?.title, source?.filename], shellSearch.value);
  });
  const dialogId = libraryFactDialogId.value;
  const editing = typeof dialogId === 'string' ? accepted.find((fact) => fact.id === dialogId) : undefined;
  return (
    <section class="library-view">
      <List gap="l" controlInsets="tb">
        <ListInsetControl>
          <Text tone="quiet" size="compact">
            What the AI knows about {libraryScope.value === '' ? 'the company' : 'this project'} on every demand.
            {libraryScope.value === ''
              ? ' Each Project Knowledge Base adds to this for demands in that project.'
              : ' The AI unions this with the Organization Knowledge Base for demands in this project.'}
          </Text>
        </ListInsetControl>
        <div class="tab-content" hidden={libraryTab.value !== 'facts'}>
          <LibraryFacts facts={facts} docs={libraryDocs.value} query={shellSearch.value} />
        </div>
        <div class="tab-content" hidden={libraryTab.value !== 'documents'}>
          <LibraryDocuments docs={libraryDocs.value} />
        </div>
      </List>
      <LibraryFactDialog fact={editing} docs={libraryDocs.value} open={dialogId !== undefined} />
      <DocumentPreviewDialog />
      {libraryRevisions.value !== null ? (
        <DialogSurface
          label="Document History"
          lightDismiss
          className="library-revisions-dialog"
          rootAttributes={{ 'data-auto-open-dialog': '' }}
          footer={
            <Button
              tone="neutral"
              size="default"
              emphasis="plain"
              rootAttributes={{ 'data-dialog-close': '', 'data-lib-revisions-close': '' }}
            >
              Close
            </Button>
          }
        >
          <List gap={space('s')}>
            {libraryRevisions.value.revisions.map((revision) => (
              <div class="library-revision-row" data-key={revision.id} data-revision-id={revision.id}>
                <Text wrap="anywhere">
                  Version {String(revision.versionNumber)} · {revision.filename}
                  {revision.isCurrent ? ' (current)' : null}
                </Text>
                <Text tone="quiet" size="compact">
                  {new Date(revision.createdAt).toLocaleDateString()}
                </Text>
                <Button
                  tone="neutral"
                  emphasis="secondary"
                  size="compact"
                  rootAttributes={{ 'data-lib-revision-download': revision.id }}
                >
                  Download
                </Button>
              </div>
            ))}
          </List>
        </DialogSurface>
      ) : null}
      {notice.value !== '' ? <StateBanner tone="info" title={notice.value} /> : null}
    </section>
  );
}
